import {
  ActionBar,
  ChakraFlex,
  ChakraGrid,
  ChakraSimpleGrid,
  FAIconName,
  FireflyButton,
  ToastMessage,
  useToastMessage,
} from '@equip.health/ui';

import { usePatientContactV2 } from '~/lib/hooks/usePatientContactV2';
import useUnsavedChangesGuard from '~/lib/hooks/useUnsavedChangesGuard';
import {
  isValidZipCodeFormat,
  validateContactPhonesField,
} from '~/lib/util/patientContact.util';

import { isEmpty } from 'lodash';
import SupportLinkedAccountsModal from '~/components/common/SupportLinkedAccountsModal';
import {
  CHANGE_USER_EMAIL_WARNING,
  EMERGENCY_CONTACT_MOBILE_REQUIRED,
  PATIENT_DATE_OF_BIRTH_REQUIRED,
  SUPPORT_EMAIL_REQUIRED,
} from '~/lib/constants/patients.constants';
import { useInviteSupportMutation } from '~/store/services/patients';
import EditDuplicateSupportModal from '../contact/edit-duplicate-support-modal/EditDuplicateSupportModal';
import EditEmailWarningModal from '../contact/EditEmailWarningModal';
import ContactAddress from './ContactAddress';
import ContactInfoSection from './ContactInfoSection';
import ContactPersonalInfo from './ContactPersonalInfo';

interface PatientAddContactInfoProps {
  contact?: PatientContact;
  onDiscard: () => void;
  onAdd: () => void;
  onDeletePatientSupportLink?: () => void;
  patientId: string;
  patientBasicDetails: Patient;
  supportEmailAddresses: string[];
}

const PatientAddContactV2 = ({
  contact,
  onAdd,
  onDiscard,
  onDeletePatientSupportLink,
  patientId,
  patientBasicDetails,
  supportEmailAddresses,
}: PatientAddContactInfoProps) => {
  const TOP_LEVEL_ID = 'patient-contact-v2';
  const {
    formData,
    isEmailSameAsExistingSupportEmail,
    isEmailSameAsPatientEmail,
    isSupportEmailEdited,
    updateField,
    updateSection,
    handleSave,
    handleSubmitContactInfo,
    updateInvalidFields,
  } = usePatientContactV2(
    contact,
    patientId,
    onAdd,
    patientBasicDetails,
    supportEmailAddresses,
    onDeletePatientSupportLink,
  );
  const { createToast } = useToastMessage();
  const [inviteSupport, { isSuccess }] = useInviteSupportMutation();

  const { UnsavedChangesModal, guardedDismiss } = useUnsavedChangesGuard({
    isDirty: formData.hasUnsavedChanges,
    onDiscard,
  });

  const shouldShowToastMessage =
    formData?.personalInfo.isGrantEquipAccess &&
    (!formData?.contactInfo.email || !formData?.phonesField.mobile);

  const shouldDisplayToast = shouldShowToastMessage || isSupportEmailEdited;

  const shouldShowEmergencyContactMobileWarning =
    formData?.personalInfo.isEmergencyContact && !formData?.phonesField.mobile;

  const isValidPhonesField = validateContactPhonesField(
    formData.phonesField,
    formData.personalInfo.isGrantEquipAccess || contact?.isSupport,
  );

  const shouldDisableSave =
    [
      ...Object.entries(formData?.personalInfo?.invalidFields || {}),
      ...Object.entries(formData?.contactInfo?.invalidFields || {}),
    ].some(
      ([key, value]) =>
        value === true &&
        (formData?.personalInfo[key as keyof ProfilePersonalInfoData] ||
          formData?.contactInfo[key as keyof ContactInfoType]),
    ) ||
    !isValidPhonesField ||
    isEmailSameAsExistingSupportEmail ||
    isEmailSameAsPatientEmail ||
    !isValidZipCodeFormat(formData?.address?.zipCode) ||
    shouldShowEmergencyContactMobileWarning;

  const shouldShowActionBar =
    contact?.isSupport && !contact?.hasSetPassword && !isSuccess;

  const actionBarDisplayName = contact?.chosenName || contact?.firstName;
  const hasPatientWithEmptyDateOfBirth = isEmpty(
    patientBasicDetails?.dateOfBirth,
  );

  const onResendEmail = () => {
    inviteSupport({ supportId: contact?.supportExternalId })
      .unwrap()
      .then(() => {
        createToast({
          icon: FAIconName.checkRegular,
          id: 'resend-mail-success_toast',
          text: 'Email resent',
          variant: 'success',
        });
      })
      .catch(() => {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: 'resend-mail-error_toast',
          text: "We're sorry, we were unable to resend email at this time. Please try again.",
          variant: 'critical',
        });
      });
  };

  return (
    <>
      {contact?.otherLinkedPatients && (
        <SupportLinkedAccountsModal
          isOpen={formData.isLinkedAccountsModalVisible}
          linkedPatients={contact?.otherLinkedPatients}
          modalId={TOP_LEVEL_ID}
          onClose={() => updateField('isLinkedAccountsModalVisible', false)}
          supportEmail={contact?.email}
        />
      )}
      <EditEmailWarningModal
        id="support"
        isOpen={formData.emailChangeWarningModalOpen}
        onClose={() => updateField('emailChangeWarningModalOpen', false)}
        onConfirm={handleSubmitContactInfo}
      />
      <ChakraSimpleGrid columns={3} marginBottom="24px" spacing="36px">
        {UnsavedChangesModal}
        <ContactPersonalInfo
          hasPatientWithEmptyDateOfBirth={hasPatientWithEmptyDateOfBirth}
          isSupportEmailEdited={isSupportEmailEdited}
          otherLinkedPatients={contact?.otherLinkedPatients}
          personalInfo={formData.personalInfo}
          updateField={updateField}
          updateInvalidFields={updateInvalidFields}
          updateSection={updateSection}
        />
        <ContactInfoSection
          contactInfo={formData.contactInfo}
          isEmailSameAsExistingSupportEmail={isEmailSameAsExistingSupportEmail}
          isEmailSameAsPatientEmail={isEmailSameAsPatientEmail}
          isGrantEquipAccess={formData.personalInfo.isGrantEquipAccess}
          isSupport={contact?.isSupport}
          otherLinkedPatients={contact?.otherLinkedPatients}
          isEmergencyContact={formData.personalInfo.isEmergencyContact}
          phoneData={formData.phonesField}
          updateField={updateField}
          updateInvalidFields={updateInvalidFields}
          updateSection={updateSection}
        />
        <ContactAddress
          address={formData.address}
          contactAddress={formData.contactAddress}
          updateField={updateField}
          updateSection={updateSection}
        />
      </ChakraSimpleGrid>

      <ChakraGrid gap="6px" marginBottom="32px">
        {hasPatientWithEmptyDateOfBirth && (
          <ToastMessage
            icon={FAIconName.exclamationTriangleRegular}
            id={`${TOP_LEVEL_ID}__date-of-birth-required-warning`}
            isFullWidth
            text={PATIENT_DATE_OF_BIRTH_REQUIRED}
            variant="critical"
          />
        )}
        {shouldShowEmergencyContactMobileWarning && (
          <ToastMessage
            icon={FAIconName.exclamationTriangleRegular}
            id={`${TOP_LEVEL_ID}__warning-emergency-contact`}
            isFullWidth
            text={EMERGENCY_CONTACT_MOBILE_REQUIRED}
            variant="critical"
          />
        )}
        {shouldDisplayToast && (
          <ToastMessage
            icon={FAIconName.exclamationTriangleRegular}
            id={`${TOP_LEVEL_ID}__warning`}
            isFullWidth
            text={
              isSupportEmailEdited
                ? CHANGE_USER_EMAIL_WARNING
                : SUPPORT_EMAIL_REQUIRED
            }
            variant="critical"
          />
        )}

        {formData.isError && (
          <ToastMessage
            icon={FAIconName.exclamationTriangleRegular}
            id={`${TOP_LEVEL_ID}__warning`}
            isFullWidth
            text={
              "We're sorry, we were unable to save your changes. Please try again."
            }
            variant="critical"
          />
        )}

        {shouldShowActionBar && (
          <ActionBar
            barIcon={FAIconName.exclamationTriangleRegular}
            buttonIcon={FAIconName.envelopeRegular}
            buttonText="Resend password creation email"
            id={`${TOP_LEVEL_ID}-no-password`}
            onButtonClick={onResendEmail}
            text={`${actionBarDisplayName} has not created a password.`}
            variant="warning"
          />
        )}
      </ChakraGrid>

      <ChakraFlex
        alignItems="center"
        justifyContent="space-between"
        marginTop="18px"
        width="100%"
      >
        <FireflyButton
          id={`${TOP_LEVEL_ID}__cancel-button`}
          leftIcon={FAIconName.XmarkRegular}
          onClick={guardedDismiss}
          variant="secondary"
        >
          Cancel
        </FireflyButton>
        <FireflyButton
          id={`${TOP_LEVEL_ID}__save-button`}
          isDisabled={!formData.hasUnsavedChanges || shouldDisableSave}
          isLoading={formData.isLoading}
          leftIcon={FAIconName.checkRegular}
          onClick={handleSave}
        >
          Save changes
        </FireflyButton>
      </ChakraFlex>
      <EditDuplicateSupportModal
        dismissModal={() => updateField('showEditDuplicateSupportModal', null)}
        onUpdate={onAdd}
        showEditDuplicateSupportModal={formData.showEditDuplicateSupportModal}
      />
    </>
  );
};

export default PatientAddContactV2;
